import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PrivateLayout from './Private/Private';
import PublicLayout from './Public/Public';
import AdminLayout from './Admin/Admin';
import OrgLayout from './OrgLayout/OrgLayout';
import Spinner from '../components/Spinner';
import { deleteLocalStorage, getToken, removeCookie } from '../utils/cookie';
import { getUserDetails } from '../features/user/userSlice';
import { ROUTELIST } from '../routes/routesList';
import {
  AUTHENTICATION_TOKEN,
  PUBLIC_DEFAULT_ROUTE,
  ORGANIZATION_PAGE_ROUTE,
  PRIVATE_DEFAULT_ROUTE,
  ADMIN_ROUTE,
  PRIVATE_ROUTE,
  PUBLIC_ROUTE,
  ORGANIZATION_ROUTE,
} from '../constants/common';
import { isValidLink } from '../utils/common';

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authenticationToken = getToken(AUTHENTICATION_TOKEN);
  const { pathname } = useLocation();
  const [layoutLoading, setLayoutLoading] = useState(true);
  const { user, userOrganizationId, loading, userPermissions } = useSelector((state) => state.user);
  const currentRoute = useMemo(() => ROUTELIST.find(({ name }) => pathname.indexOf(name) > -1), [pathname]);

  useEffect(() => {
    if (user && userOrganizationId) {
      const params = {
        username: user?.username,
        organization_id: userOrganizationId,
      };

      dispatch(getUserDetails(params)).then(({ payload }) => {
        if (payload?.status_code === 401) {
          setLayoutLoading(false);
          deleteLocalStorage(AUTHENTICATION_TOKEN);
          removeCookie(AUTHENTICATION_TOKEN);
          localStorage.removeItem('persist:user');
          navigate(PUBLIC_DEFAULT_ROUTE);
        } else {
          setLayoutLoading(false);
        }
      });
    } else {
      setLayoutLoading(false);
    }
  }, [user, userOrganizationId, setLayoutLoading]);

  if (layoutLoading || loading) {
    return (
      <div className="spinner-wrapper">
        <Spinner />
      </div>
    );
  }

  if (pathname === '/') {
    if (!userOrganizationId && !user && !authenticationToken) {
      return <Navigate to={PUBLIC_DEFAULT_ROUTE} />;
    } else if (!userOrganizationId && user && authenticationToken) {
      return <Navigate to={ORGANIZATION_PAGE_ROUTE} />;
    } else {
      return <Navigate to={PRIVATE_DEFAULT_ROUTE} />;
    }
  }

  if (currentRoute?.routeType === ADMIN_ROUTE) {
    if (userOrganizationId && user && authenticationToken) {
      return (
        <AdminLayout>
          <Outlet />
        </AdminLayout>
      );
    }
    return <Navigate to="/" />;
  }

  if (currentRoute?.routeType === PRIVATE_ROUTE) {
    if (userOrganizationId && user && authenticationToken) {
      return (
        <>
          <PrivateLayout routeName={currentRoute?.name}>
            <Outlet />
          </PrivateLayout>
        </>
      );
    }
  }

  if (currentRoute?.routeType === PUBLIC_ROUTE) {
    if (pathname.startsWith('/login') && userOrganizationId && user && authenticationToken) {
      const continueUrl = new URLSearchParams(window.location.search).get('continue');
      const isValidContinueUrl = isValidLink(continueUrl);
      return <Navigate to={isValidContinueUrl && continueUrl ? continueUrl : PRIVATE_DEFAULT_ROUTE} />;
    }
    return (
      <PublicLayout>
        <Outlet />
      </PublicLayout>
    );
  }

  if (currentRoute?.routeType === ORGANIZATION_ROUTE) {
    if (user && authenticationToken) {
      return (
        <OrgLayout>
          <Outlet />
        </OrgLayout>
      );
    }
  }

  let publicLinkWithRedirect = PUBLIC_DEFAULT_ROUTE;
  if (pathname !== PUBLIC_DEFAULT_ROUTE) {
    const appendUrl = `?continue=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    publicLinkWithRedirect += appendUrl;
  }

  return <Navigate to={publicLinkWithRedirect} />;
};

export default Layout;
